import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import AppTile, { openAppNewTab } from "@/components/AppTile";
import { formatApiError } from "@/lib/errors";

function compareApps(a, b) {
  const la = a.last_launched_at ? new Date(a.last_launched_at).getTime() : 0;
  const lb = b.last_launched_at ? new Date(b.last_launched_at).getTime() : 0;
  if (la !== lb) return lb - la; // most recent first
  return a.name.localeCompare(b.name);
}

export default function AppsLauncher() {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/apps/mine");
        if (!alive) return;
        setApps(data);
      } catch (e) {
        if (alive) toast.error(formatApiError(e) || "Failed to load apps");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = needle
      ? apps.filter(
          (a) =>
            a.name.toLowerCase().includes(needle) ||
            (a.description || "").toLowerCase().includes(needle)
        )
      : apps.slice();
    return list.sort(compareApps);
  }, [apps, q]);

  const launch = (app) => {
    if (app.embeddable) {
      navigate(`/apps/${app.id}/embed`);
    } else {
      openAppNewTab(app);
    }
  };

  return (
    <div data-testid="apps-launcher" className="mx-auto w-full max-w-6xl">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
            Apps
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            {apps.length
              ? `${apps.length} app${apps.length === 1 ? "" : "s"} available to you.`
              : "No apps assigned yet."}
          </p>
        </div>
        <div className="relative w-full max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search apps"
            className="pl-9"
            data-testid="apps-search"
          />
        </div>
      </div>

      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-28 animate-pulse rounded-xl border border-slate-200 bg-white/60"
            />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div
          data-testid={q ? "apps-empty-no-match" : "apps-empty-no-search"}
          className="rounded-xl border border-dashed border-slate-200 bg-white p-10 text-center"
        >
          <div className="font-display text-base font-semibold text-slate-800">
            No apps available
          </div>
          <p className="mt-1 text-sm text-slate-500">
            {q ? "Nothing matches your search." : "Contact your administrator."}
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((app) => (
            <AppTile key={app.id} app={app} onClick={launch} />
          ))}
        </div>
      )}
    </div>
  );
}
